import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  BedDouble,
  Building2,
  CalendarCheck2,
  CircleHelp,
  LogOut,
  Menu as MenuIcon,
  Search,
  UserRound,
} from 'lucide-react';
import logo from '../../assets/images/Nappr-Logo-BlackZs.png';
import { cn } from '../../lib/cn';
import { useScrolled } from '../../lib/hooks';
import { useSession } from '../../state/session';
import { Avatar, Button, Drawer, IconButton, Menu } from '../../ui';
import { useNearbySearchLink } from '../../features/search/useNearbySearchLink';
import styles from './Header.module.css';

const NAV = [
  { key: 'find', label: 'Find a room', icon: Search },
  { key: 'host', label: 'List your space', icon: Building2, to: '/self-host' },
  { key: 'help', label: 'Help', icon: CircleHelp, to: '/faq' },
];

function UserMenu() {
  const { user, signOut, canAccessHostPortal } = useSession();
  const navigate = useNavigate();

  const items = [
    ...(canAccessHostPortal
      ? [{ key: 'host', label: 'Host dashboard', icon: <Building2 />, to: '/self-host' }]
      : []),
    { key: 'bookings', label: 'My bookings', icon: <CalendarCheck2 />, to: '/bookings' },
    { key: 'profile', label: 'Profile', icon: <UserRound />, to: '/profile' },
    { key: 'd1', divider: true },
    {
      key: 'logout',
      label: 'Sign out',
      icon: <LogOut />,
      onSelect: () => {
        signOut();
        navigate('/');
      },
    },
  ];

  return (
    <Menu
      label="Account"
      items={items}
      header={
        <div className={styles.menuHeader}>
          <p className={styles.menuName}>
            {user.firstname} {user.lastname}
          </p>
          <p className={styles.menuEmail}>{user.email}</p>
        </div>
      }
      renderLink={(item, className, content) => (
        <Link to={item.to} className={className} role="menuitem">
          {content}
        </Link>
      )}
      trigger={({ toggle, triggerProps }) => (
        <button type="button" className={styles.userButton} onClick={toggle} {...triggerProps}>
          <Avatar firstName={user.firstname} lastName={user.lastname} size={32} />
          <span className={styles.userName}>{user.firstname}</span>
        </button>
      )}
    />
  );
}

function MobileMenu({ open, onClose, findLink }) {
  const { user, isSignedIn, signOut, openAuth, canAccessHostPortal } = useSession();
  const navigate = useNavigate();

  const go = (to, state) => {
    onClose();
    navigate(to, state ? { state } : undefined);
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      side="left"
      width={340}
      header={
        <div className={styles.drawerHeader}>
          <img src={logo} alt="Nappr" className={styles.drawerLogo} />
        </div>
      }
    >
      <nav className={styles.drawerNav} aria-label="Main">
        <button type="button" className={styles.drawerLink} onClick={() => go(findLink.to, findLink.state)}>
          <Search /> Find a room
        </button>
        <button type="button" className={styles.drawerLink} onClick={() => go('/self-host')}>
          <Building2 /> List your space
        </button>
        <button type="button" className={styles.drawerLink} onClick={() => go('/faq')}>
          <CircleHelp /> Help &amp; FAQ
        </button>
      </nav>

      <div className={styles.drawerSection}>
        {isSignedIn ? (
          <>
            <div className={styles.drawerUser}>
              <Avatar firstName={user.firstname} lastName={user.lastname} size={40} />
              <div>
                <p className={styles.menuName}>
                  {user.firstname} {user.lastname}
                </p>
                <p className={styles.menuEmail}>{user.email}</p>
              </div>
            </div>
            {canAccessHostPortal && (
              <button type="button" className={styles.drawerLink} onClick={() => go('/self-host')}>
                <Building2 /> Host dashboard
              </button>
            )}
            <button type="button" className={styles.drawerLink} onClick={() => go('/bookings')}>
              <CalendarCheck2 /> My bookings
            </button>
            <button type="button" className={styles.drawerLink} onClick={() => go('/profile')}>
              <UserRound /> Profile
            </button>
            <button
              type="button"
              className={styles.drawerLink}
              onClick={() => {
                signOut();
                go('/');
              }}
            >
              <LogOut /> Sign out
            </button>
          </>
        ) : (
          <div className={styles.drawerAuth}>
            <Button
              block
              size="lg"
              onClick={() => {
                onClose();
                openAuth({ view: 'login' });
              }}
            >
              Sign in
            </Button>
            <Button
              block
              size="lg"
              variant="secondary"
              onClick={() => {
                onClose();
                openAuth({ view: 'register' });
              }}
            >
              Create an account
            </Button>
          </div>
        )}
      </div>

      <div className={styles.drawerFooter}>
        <Link to="/booking-policy" onClick={onClose}>
          Booking policy
        </Link>
        <Link to="/contact-us" onClick={onClose}>
          Contact
        </Link>
        <Link to="/terms" onClick={onClose}>
          Terms
        </Link>
        <Link to="/privacy" onClick={onClose}>
          Privacy
        </Link>
      </div>
    </Drawer>
  );
}

export function Header({ overlay = false }) {
  const { isSignedIn, openAuth } = useSession();
  const scrolled = useScrolled(12);
  const [menuOpen, setMenuOpen] = useState(false);
  const findLink = useNearbySearchLink();
  const { pathname } = useLocation();

  useEffect(() => setMenuOpen(false), [pathname]);

  const transparent = overlay && !scrolled;

  return (
    <header className={cn(styles.header, transparent ? styles.transparent : styles.solid)}>
      <div className={cn('container-wide', styles.bar)}>
        <Link to="/" className={styles.brand} aria-label="Nappr home">
          <img src={logo} alt="Nappr" width="112" height="46" className={styles.logo} />
        </Link>

        <nav className={styles.nav} aria-label="Main">
          {NAV.map(({ key, label, icon: Icon, to }) =>
            key === 'find' ? (
              <Link key={key} to={findLink.to} state={findLink.state} className={styles.navLink}>
                <Icon aria-hidden="true" />
                {label}
              </Link>
            ) : (
              <NavLink
                key={key}
                to={to}
                className={({ isActive }) => cn(styles.navLink, isActive && styles.navActive)}
              >
                <Icon aria-hidden="true" />
                {label}
              </NavLink>
            )
          )}
        </nav>

        <div className={styles.actions}>
          {isSignedIn ? (
            <UserMenu />
          ) : (
            <>
              <Button variant="ghost" size="sm" className={styles.desktopOnly} onClick={() => openAuth({ view: 'register' })}>
                Sign up
              </Button>
              <Button variant="dark" size="sm" onClick={() => openAuth({ view: 'login' })} iconLeft={<BedDouble />}>
                Sign in
              </Button>
            </>
          )}
          <IconButton label="Open menu" className={styles.menuButton} onClick={() => setMenuOpen(true)}>
            <MenuIcon />
          </IconButton>
        </div>
      </div>

      <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} findLink={findLink} />
    </header>
  );
}
